'use client';

import { useEffect, useMemo, useState, type RefObject } from 'react';

/** A fresh observation session cannot inherit visibility from a prior active state. */
export function useAgentActivityInView(
  ref: RefObject<Element | null>,
  enabled: boolean
): boolean {
  const session = useMemo(() => ({ enabled }), [enabled]);
  const [observation, setObservation] = useState<{
    session: typeof session | null;
    inView: boolean;
  }>({ session: null, inView: false });

  useEffect(() => {
    const element = ref.current;
    if (
      !session.enabled ||
      !element ||
      typeof IntersectionObserver === 'undefined'
    )
      return;
    let active = true;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries.findLast(
        (candidate) => candidate.target === element
      );
      if (active && entry)
        setObservation({ session, inView: entry.isIntersecting });
    });
    observer.observe(element);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [ref, session]);

  return enabled && observation.session === session && observation.inView;
}
